import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { OperaProvider } from "./context/OperaContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTopButton from "./components/ScrollToTopButton";
import ErrorBoundary from "./components/ErrorBoundary";
import Admin from "./pages/Admin/Admin";
import AdminLogin from "./pages/Admin/AdminLogin";
import OperaCities from "./pages/OperaCities";
import OperaHousesByCity from "./pages/OperaHousesByCity";
import OperaHousePage from "./pages/OperaHousePage";
import Performers from "./pages/Performers";
import PerformerPage from "./pages/PerformerPage";
import Composers from "./pages/Composers";
import ComposerPage from "./pages/ComposerPage";
import EventPage from "./pages/Event/EventPage";
import Appointment from "./pages/Appointments";
import Newsletter from "./pages/Newsletter";
import Home from "./pages/Home/Home";
import BookingPage from "./pages/Booking/BookingPage";
import About from "./pages/About";
import PrivacyPolicy from "./pages/DataPrivacy";
import Impressum from "./pages/Impressum";
import InsuranceConditions from "./pages/InsuranceConditions";
import PictureCredits from "./pages/PictureCredits";
import TravelConditions from "./pages/TravelConditions";
import Reviews from "./pages/Reviews";
import TravelInsurance from "./pages/TravelInsurance";
import NewsletterSubscriptionSuccess from "./pages/NewsletterSubscriptionSuccess";
import BookingSuccess from "./pages/BookingSuccess/BookingSuccess";
import FAQs from "./pages/FAQs";

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Handle scroll behavior based on pathname and hash
    if (pathname === "/" || pathname === "/home") {
      if (hash) {
        // If there's a hash on the home page, scroll to that element
        setTimeout(() => {
          const id = hash.replace("#", "");
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      } else {
        // No hash, scroll to top
        window.scrollTo(0, 0);
      }
    } else {
      // For other pages, always scroll to top with a small delay to ensure content is rendered
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  }, [pathname, hash]);

  return null;
}

function HeaderWrapper({ children }) {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith("/admin");
  const isBookingPage = location.pathname.startsWith("/booking");

  return (
    <div
      className={`${
        isAdminPage ? "bg-black" : isBookingPage ? "bg-gray-50" : ""
      }`}
    >
      <Header />
      {children}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <OperaProvider>
        <Router>
          <ScrollToTop />
          <HeaderWrapper>
            <ErrorBoundary>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/home" element={<Home />} />
                <Route
                  path="/admin"
                  element={
                    <div className="pt-[60px]">
                      <Admin />
                    </div>
                  }
                />
                <Route
                  path="/admin/login"
                  element={
                    <div className="pt-[60px]">
                      <AdminLogin />
                    </div>
                  }
                />
                <Route
                  path="/opera-houses-and-festivals"
                  element={<OperaCities />}
                />
                <Route
                  path="/opera-houses-and-festivals/:city"
                  element={<OperaHousesByCity />}
                />
                <Route path="/performers" element={<Performers />} />
                <Route
                  path="/performers/:performerName"
                  element={<PerformerPage />}
                />
                <Route path="/composers" element={<Composers />} />
                <Route
                  path="/composers/:composerName"
                  element={<ComposerPage />}
                />
                <Route path="/appointments" element={<Appointment />} />
                <Route path="/newsletter" element={<Newsletter />} />
                <Route path="/booking" element={<BookingPage />} />
                <Route path="/thank-you" element={<BookingSuccess />} />
                <Route
                  path="/thank-you-newsletter"
                  element={<NewsletterSubscriptionSuccess />}
                />
                <Route path="/welcome" element={<About />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/imprint" element={<Impressum />} />
                <Route
                  path="/insurance-conditions"
                  element={<InsuranceConditions />}
                />
                <Route path="/image-credits" element={<PictureCredits />} />
                <Route
                  path="/terms-conditions"
                  element={<TravelConditions />}
                />
                <Route
                  path="/travel-cancellation-insurance"
                  element={<TravelInsurance />}
                />
                <Route path="/testimonials" element={<Reviews />} />
                <Route path="/FAQs" element={<FAQs />} />
                <Route
                  path="/:operaHouseName/:eventSlug"
                  element={<EventPage />}
                />
                <Route path="/:operaHouseName" element={<OperaHousePage />} />
                <Route
                  path="*"
                  element={
                    <div className="text-center pt-[60px]">
                      <h1 className="text-2xl text-black my-8">
                        Page Not Found
                      </h1>
                    </div>
                  }
                />
              </Routes>
            </ErrorBoundary>
            <ScrollToTopButton />
            <Footer />
          </HeaderWrapper>
        </Router>
      </OperaProvider>
    </HelmetProvider>
  );
}

export default App;
